"use client";

import { MotionDiv, useInView } from "@/lib/framer";
import { Container } from "@/components/container";
import ArrowBottom from "@/components/ui/arrow-bottom";
import { Bot, CheckIcon } from "lucide-react";
import { useRef } from "react";
import ArrowBottomFlipped from "./ui/arrow-bottom-flipped";

interface FeatureBucketProps {
  number: number;
  videoPath: string;
  title: string;
  capability: string;
  description: string;
  features: string[];
  delay: number;
}

function FeatureVideo({ videoPath }: { videoPath: string }) {
  return (
    <MotionDiv className="flex items-center justify-center" whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
      <video className="h-auto w-full" autoPlay muted loop playsInline>
        <source src={videoPath} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </MotionDiv>
  );
}

function FeatureBucket({ number, videoPath, title, capability, description, features, delay }: FeatureBucketProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isEven = number % 2 === 0;

  return (
    <MotionDiv
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay }}
      className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8"
    >
      <div className="grid grid-cols-1 items-center gap-8 md:gap-12 lg:grid-cols-2">
        <div
          className={`${isEven ? "lg:hidden" : "lg:order-1 lg:block"} border-feature-secondary shadow-feature-secondary/50 hover:border-feature-primary/20 hover:shadow-feature-primary/10 overflow-hidden rounded-lg border-2 shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-lg`}
        >
          <FeatureVideo videoPath={videoPath} />
        </div>

        <div className={`space-y-6 md:space-y-8 ${!isEven ? "lg:order-2" : "lg:order-1"}`}>
          <div className="space-y-4">
            <p className="text-feature-primary inline-flex items-center text-lg font-semibold">
              <span className="bg-feature-secondary text-feature-primary mr-2 flex h-8 w-8 items-center justify-center rounded-full">
                {number}
              </span>
              {title}
            </p>
            <div className="space-y-3">
              <h3 className="text-foreground text-3xl tracking-tight sm:text-4xl">{capability}</h3>
              <p className="text-muted-foreground text-lg leading-relaxed sm:text-xl">{description}</p>
            </div>
          </div>

          <div className="space-y-4">
            {features.map((feature, index) => (
              <MotionDiv
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.3, delay: index * 0.1 + delay }}
                className="flex items-start gap-x-3"
              >
                <span className="flex h-[1lh] items-center">
                  <CheckIcon className="text-[#E6A91A] size-5 shrink-0 sm:size-7" />
                </span>
                <span className="text-muted-foreground text-base sm:text-lg">{feature}</span>
              </MotionDiv>
            ))}
          </div>
        </div>

        {isEven && (
          <div className="border-feature-secondary shadow-feature-secondary/50 hover:border-feature-primary/20 hover:shadow-feature-primary/10 hidden overflow-hidden rounded-lg border-2 shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-lg lg:order-3 lg:block">
            <FeatureVideo videoPath={videoPath} />
          </div>
        )}
      </div>
    </MotionDiv>
  );
}

function ArrowDivider({ flipped = false }) {
  return (
    <MotionDiv
      className="mt-12 flex justify-center md:mt-16"
      whileHover={{ scale: 1.1 }}
      transition={{ duration: 0.3 }}
    >
      {flipped ? <ArrowBottomFlipped /> : <ArrowBottom />}
    </MotionDiv>
  );
}

export default function FeatureSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const features = [
    {
      number: 1,
      videoPath: "/videos/feature-bucket-1.mp4",
      title: "Easy signup",
      capability: "Get started in 5 minutes",
      description:
        "Start automating your development workflow with a seamless onboarding experience. No complex setup required.",
      features: [
        "Sign up with your GitHub account",
        "Select your repositories for automation",
        "Select a pricing option",
      ],
    },
    {
      number: 2,
      videoPath: "/videos/feature-bucket-2.mp4",
      title: "Code change digests",
      capability: "Ditch your daily standups",
      description:
        "Get daily summaries of your codebase changes, automatically categorized and analyzed for impact. Save time and keep everyone aligned.",
      features: ["AI-powered code analysis", "Automatic categorization", "Bridge the gap between tech and business"],
    },
    {
      number: 3,
      videoPath: "/videos/feature-bucket-3.mp4",
      title: "Slack integration",
      capability: "Get notified where work happens",
      description:
        "Receive automated updates directly in your team's Slack channel. Keep everyone informed without switching context.",
      features: [
        "One-click setup",
        "Select your preferred notification channel",
        "Receive regular code change digests",
      ],
    },
    {
      number: 4,
      videoPath: "/videos/feature-bucket-4.mp4",
      title: "Analytics & trends",
      capability: "Spot code bottlenecks and keep your releases on track",
      description:
        "Leverage interactive analytics charts to pinpoint code hotspots, measure your team's velocity, and consistently deliver on schedule.",
      features: ["Daily code graphs", "Team performance metrics", "Historical trend monitoring"],
    },
    {
      number: 5,
      videoPath: "/videos/feature-bucket-5.mp4",
      title: "Multi-user organization accounts",
      capability: "Scale with your team",
      description:
        "Manage your entire organization's development insights from a single dashboard. Perfect for teams of any size.",
      features: ["No per-seat pricing", "Give access to your entire team", "Invite multiple team members at once"],
    },
  ];

  return (
    <>
      <Container className="py-16 sm:py-20 lg:py-24">
        <MotionDiv
          ref={sectionRef}
          className="space-y-12 md:space-y-16"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="grid grid-cols-1 items-center gap-8 md:gap-12 lg:grid-cols-2">
            <MotionDiv
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center lg:order-last"
            >
              <div className="from-feature-subtle to-feature-secondary rounded-full bg-gradient-to-br p-8 shadow-lg">
                <Bot className="text-feature-primary h-20 w-20 sm:h-24 sm:w-24" />
              </div>
            </MotionDiv>

            <MotionDiv
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <p className="text-feature-primary text-sm font-semibold tracking-wider uppercase">THE SOLUTION</p>
              <h2 className="text-foreground text-4xl tracking-tight text-balance sm:text-5xl lg:text-6xl">
                Automate standups and boost your productivity
              </h2>
              <p className="text-muted-foreground max-w-2xl text-lg leading-relaxed sm:text-xl">
                While others rely on manual updates and meetings, Git Digest uses advanced AI to automatically analyze
                your codebase, delivering rich, contextual summaries. Our unique approach combines code analysis with
                natural language processing to surface insights that matter, transforming how engineering teams
                collaborate.
              </p>
            </MotionDiv>
          </div>
        </MotionDiv>
      </Container>

      {features.map((feature, index) => (
        <Container
          key={index}
          className={`bg-gradient-to-b py-16 sm:py-20 lg:py-24 ${index % 2 === 0 ? "from-background to-feature-subtle" : "from-feature-subtle to-background"}`}
        >
          <FeatureBucket {...feature} delay={0.2} />
          {index < features.length - 1 && <ArrowDivider flipped={index % 2 !== 0} />}
          {index === features.length - 1 && <ArrowDivider flipped={index % 2 !== 0} />}
        </Container>
      ))}
    </>
  );
}
